import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy, DeleteOutline } from "@mui/icons-material";
import LinearProgress from "components/LinearProgress";
import StyledMainButton from "components/buttons/StyledMainButton";
import { useFetch, useActionWithPath } from "utils/query/query";
import {
  AccessKey,
  ASSIGNABLE_ROLES,
  CreateFailure,
  DEFAULT_EXPIRATION_SECONDS,
  EXPIRY_OPTIONS,
  classifyCreateFailure,
  describeCreateFailure,
  expiryOf,
  formatCreated,
  formatExpiry,
  rolesToSubmit,
  shortId,
  sortKeys,
} from "./accessKeys";

const KEYS_PATH = "/organization/keys";

/**
 * Access keys for the active organization.
 *
 * A key is a machine credential that carries organization roles, so this screen is really a
 * permission-granting surface wearing a table. Three things follow from that and shape the design:
 *
 *   - The token appears exactly once, in a dialog that has to be dismissed deliberately. Nothing
 *     recovers it afterwards, because the server keeps only a BCrypt hash of the secret behind it.
 *   - Revoking asks for confirmation and names the key, since a key in use is something running
 *     somewhere that will start failing.
 *   - An environment without a signing secret is explained, not reported as an error. That is
 *     beta's deliberate configuration, and a red failure would send someone hunting a bug that
 *     does not exist.
 */
export const AccessKeysTab = () => {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [expirySeconds, setExpirySeconds] = useState<number>(
    DEFAULT_EXPIRATION_SECONDS
  );
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [failure, setFailure] = useState<CreateFailure | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<AccessKey | null>(null);

  const {
    data: keys = [],
    isFetching,
    refetch,
  } = useFetch<AccessKey[]>(KEYS_PATH, {});

  const rows = useMemo(() => sortKeys(keys), [keys]);

  const readFailure = async (error: any): Promise<CreateFailure> => {
    // Errors arrive as the raw Response, so the server's explanation has to be read off the body.
    let message: string | undefined;
    try {
      const body = await error?.clone?.().json?.();
      message = body?.message;
    } catch {
      message = undefined;
    }
    return classifyCreateFailure(error?.status, message);
  };

  const createAction = useActionWithPath(
    {
      onSuccess: (token: string) => {
        setCreateOpen(false);
        setSelectedRoles([]);
        setTokenCopied(false);
        setIssuedToken(token);
        refetch();
      },
      onError: async (error: any) => setFailure(await readFailure(error)),
    },
    // The endpoint returns the token as a bare string, not JSON.
    true
  );

  const revokeAction = useActionWithPath({
    onSuccess: () => {
      setPendingRevoke(null);
      refetch();
    },
    onError: async (error: any) => {
      setPendingRevoke(null);
      setFailure(await readFailure(error));
    },
  });

  const createKey = () => {
    setFailure(null);
    // @ts-ignore mutate params are loosely typed across this codebase
    createAction.mutate({
      method: "post",
      path: `${KEYS_PATH}?expirationSeconds=${expirySeconds}`,
      body: JSON.stringify({ roles: rolesToSubmit(selectedRoles) }),
    });
  };

  const revokeKey = (key: AccessKey) => {
    setFailure(null);
    // @ts-ignore mutate params are loosely typed across this codebase
    revokeAction.mutate({
      method: "delete",
      path: `${KEYS_PATH}?keyId=${encodeURIComponent(key.id)}`,
      body: "",
    });
  };

  const copyToken = () => {
    if (issuedToken) {
      navigator.clipboard.writeText(issuedToken).then(
        () => setTokenCopied(true),
        () => setTokenCopied(false)
      );
    }
  };

  const toggleRole = (role: string) =>
    setSelectedRoles((current) =>
      current.includes(role)
        ? current.filter((r) => r !== role)
        : [...current, role]
    );

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Access Keys
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Credentials for scripts and services. A key acts with the roles you
            give it, so treat one as you would a member of your organization.
          </Typography>
        </Box>
        <StyledMainButton onClick={() => setCreateOpen(true)}>
          Create Key
        </StyledMainButton>
      </Stack>

      {failure && (
        <Alert
          severity={failure.kind === "unconfigured" ? "info" : "error"}
          onClose={() => setFailure(null)}
          sx={{ mb: 2 }}
        >
          {describeCreateFailure(failure)}
        </Alert>
      )}

      {isFetching && <LinearProgress />}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !isFetching && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No access keys yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((key) => (
              <TableRow key={key.id} hover>
                <TableCell>
                  <Tooltip title={key.id}>
                    <Typography variant="body2" fontFamily="monospace">
                      {shortId(key.id)}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {(key.roles ?? []).map((role) => (
                      <Chip key={role} label={role} size="small" />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>{formatCreated(key)}</TableCell>
                <TableCell>{formatExpiry(expiryOf(key))}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Revoke">
                    <IconButton
                      size="small"
                      onClick={() => setPendingRevoke(key)}
                      aria-label={`Revoke key ${key.id}`}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        An expiry is recorded against a key but is not checked when the key is
        used. Revoking is what stops a key working.
      </Typography>

      {/* Create */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create access key</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The token is shown once, when the key is created. It cannot be
            retrieved afterwards.
          </DialogContentText>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Roles
          </Typography>
          <FormControlLabel
            control={<Checkbox checked disabled />}
            label="COMPANY_USER (always included)"
          />
          {ASSIGNABLE_ROLES.map((role) => (
            <FormControlLabel
              key={role}
              control={
                <Checkbox
                  checked={selectedRoles.includes(role)}
                  onChange={() => toggleRole(role)}
                />
              }
              label={role}
            />
          ))}

          <FormControl fullWidth size="small" sx={{ mt: 3 }}>
            <InputLabel>Expires after</InputLabel>
            <Select
              label="Expires after"
              value={expirySeconds}
              onChange={(e) => setExpirySeconds(Number(e.target.value))}
            >
              {EXPIRY_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.seconds}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <StyledMainButton
            onClick={createKey}
            disabled={createAction.isLoading}
          >
            Create
          </StyledMainButton>
        </DialogActions>
      </Dialog>

      {/* The token, shown once */}
      <Dialog
        open={Boolean(issuedToken)}
        onClose={() => setIssuedToken(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Copy your token now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This is the only time this token is shown. Closing this dialog
            discards it, and the key has to be recreated if it is lost.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              value={issuedToken ?? ""}
              multiline
              minRows={3}
              fullWidth
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: 12 },
              }}
            />
            <Tooltip title={tokenCopied ? "Copied" : "Copy"}>
              <IconButton onClick={copyToken} aria-label="Copy token">
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions>
          <StyledMainButton onClick={() => setIssuedToken(null)}>
            Done
          </StyledMainButton>
        </DialogActions>
      </Dialog>

      {/* Revoke */}
      <Dialog
        open={Boolean(pendingRevoke)}
        onClose={() => setPendingRevoke(null)}
      >
        <DialogTitle>Revoke this key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Anything still using{" "}
            <Box component="span" fontFamily="monospace">
              {pendingRevoke?.id}
            </Box>{" "}
            will stop working immediately. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevoke(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={revokeAction.isLoading}
            onClick={() => pendingRevoke && revokeKey(pendingRevoke)}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
